import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import UserInfo from '@medflex/components/stories/UserInfo';
import { Role, Title, Subtitle } from '@medflex/components/stories/HighlightableInfos';
import { useTranslation } from 'react-i18next';
import CopyButton from '@medflex/components/stories/CopyButton/CopyButton';
import {
  ChatBubbleLeftIcon,
  VideoCameraIcon,
  PhoneIcon,
  EllipsisHorizontalIcon,
  IdentificationIcon,
  ShareIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';
import PopupMenu from '@medflex/components/stories/PopupMenu';
import { gdt } from '@medflex/shared-utils';
import { IconLoadBalancer } from '@medflex/components/stories/Icons';
import MainRepresentativeBadge from '@medflex/components/stories/MainRepresentativeBadge';
import useAddressBookContacts from '../../hooks/useAddressBookContacts';
import useOnlineStatus from '../../hooks/useOnlineStatus';
import useUserProfile from '../../hooks/useUserProfile';
import getSubtitle from '../../../utils/getSubtitle';
import useModal from '../../hooks/useModal';
import { MODALS } from '../../hooks/useIsModal';
import VideoMenuModule from '../VideoMenuModule';
import useChatContacts from '../../hooks/useChatContacts';
import SubContacts from '../SubContacts';
import ContactInfoModule from '../ContactInfoModule/ContactInfoModule';
import isSubContactId from '../../../utils/isSubContactId';
import useGdt from '../../hooks/useGdt';

const IconButton = forwardRef(({ Icon, text, onClick }, ref) => (
  <button
    type="button"
    className="flex flex-col items-center group w-20"
    onClick={onClick}
    ref={ref}
  >
    <div className="inline-flex h-10 w-10 rounded-full text-white bg-blue-500 group-hover:bg-blue-700 items-center justify-center">
      <Icon className="w-6 h-6" />
    </div>
    <span className="text-blue-500 group-hover:text-blue-700 text-xs">{text}</span>
  </button>
));

IconButton.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

const ContactOverviewModule = () => {
  const { contactId } = useParams();
  const { findOneAddressBookContactByContactId } = useAddressBookContacts();
  const { onlineUserIds } = useOnlineStatus();
  const { userProfile } = useUserProfile();
  const { t } = useTranslation('global');
  const history = useHistory();
  const { open: openModal } = useModal();
  const { toggleArchive } = useChatContacts();
  const [isEdit, setIsEdit] = useState(false);
  const [isContactInfoExpanded, setIsContactInfoExpanded] = useState(true);

  const contact = findOneAddressBookContactByContactId(contactId);
  const { canOpenInPVS, handleOpenInPVS } = useGdt(contact?.pvsId);

  if (!contact) return null;

  const { user, mainContact, subContacts } = contact;
  const isOnline = onlineUserIds.includes(user.id);
  const subTitle = getSubtitle(contact, userProfile, t);
  const copyValue = `${user.lastName}, ${user.firstName}${user.dateOfBirth ? `; ${user.dateOfBirth.split('-').reverse().join('.')}` : ''}`;

  const handleClickMessage = () => history.push(`/chat/${contact.id}`);

  const handleClickOpenPatientFile = () => handleOpenInPVS();

  const handleClickCreateSubcontact = () =>
    openModal({ modal: MODALS.createSubcontact, contactId: contact.id });

  const handleClickSharePatientContact = () =>
    openModal({ modal: MODALS.sharePatientContact, contactUserId: contact.user.id });

  const handleClickArchivePatient = () => toggleArchive(contact);

  const handleEdit = () => setIsEdit(true);

  const handleCancelEdit = () => setIsEdit(false);

  const handleAfterSubmit = () => setIsEdit(false);

  return (
    <div data-component="ContactOverviewModule">
      <div className="flex flex-row justify-center mb-4">
        <UserInfo
          avatarSrc={user.avatar}
          avatarInitials={user.initials}
          title={<Title contact={contact} />}
          isOnline={isOnline}
          role={<Role contact={contact} />}
          subtitle={<Subtitle contact={contact}>{subTitle}</Subtitle>}
          avatarSize="16"
          titleCn="text-lg font-semibold"
          subtitleCn="text-base"
          deactivated={user.deactivated}
          className={!contact.pairedAt ? 'opacity-65' : ''}
        />
        <div className="ml-6 self-center">
          <CopyButton value={copyValue} />
        </div>
      </div>
      {(mainContact || subContacts?.length > 0) && (
        <div className="flex justify-center mb-4">
          {mainContact && (
            <MainRepresentativeBadge
              userDetails={mainContact}
              href={mainContact.id ? `/address-book/${mainContact.id}/overview` : undefined}
            />
          )}
          <SubContacts items={subContacts} linkTo="address-book" />
        </div>
      )}
      {contact.pairedAt && (
        <div className="flex flex-row mx-auto justify-center space-x-4 xl:space-x-8 mb-6">
          <IconButton
            Icon={ChatBubbleLeftIcon}
            text={t('contact_overview.button_message')}
            onClick={handleClickMessage}
          />
          {!mainContact && (
            <VideoMenuModule canCall id={user.id}>
              {({ triggerRef }) => (
                <IconButton
                  ref={triggerRef}
                  Icon={VideoCameraIcon}
                  text={t('contact_overview.button_video')}
                />
              )}
            </VideoMenuModule>
          )}
          {user.phoneNumbers?.length > 0 && (
            <PopupMenu>
              <PopupMenu.Menu>
                {user.phoneNumbers.map(({ number }) => (
                  <PopupMenu.Item key={number}>
                    <a rel="noreferrer" href={`tel:${number}`}>
                      <span className="truncate">{number}</span>
                    </a>
                  </PopupMenu.Item>
                ))}
              </PopupMenu.Menu>
              <PopupMenu.Trigger>
                {({ triggerRef }) => (
                  <IconButton
                    ref={triggerRef}
                    Icon={PhoneIcon}
                    text={t('contact_overview.button_call')}
                  />
                )}
              </PopupMenu.Trigger>
            </PopupMenu>
          )}

          {canOpenInPVS && (
            <IconButton
              Icon={IdentificationIcon}
              text={t('contact_overview.button_open_pvs')}
              onClick={handleClickOpenPatientFile}
            />
          )}
          <PopupMenu>
            <PopupMenu.Menu>
              {!isSubContactId(user.id) && (
                <>
                  <PopupMenu.Item onClick={handleClickCreateSubcontact}>
                    <IconLoadBalancer size="text" />
                    <span className="ml-4">
                      {t('contact_overview.button_more.create_subcontact')}
                    </span>
                  </PopupMenu.Item>
                  <PopupMenu.Item onClick={handleClickSharePatientContact}>
                    <ShareIcon className="w-text h-text" />
                    <span className="ml-4">{t('contact_overview.button_more.share_patient')}</span>
                  </PopupMenu.Item>
                </>
              )}
              <PopupMenu.Item onClick={handleClickArchivePatient}>
                {contact.archivedAt ? (
                  <ArrowPathIcon className="h-text w-text" />
                ) : (
                  <ArchiveBoxIcon className="h-text w-text" />
                )}
                <span className="ml-4">
                  {t(
                    `contact_overview.button_more.${contact.archivedAt ? 'dearchive' : 'archive'}`,
                  )}
                </span>
              </PopupMenu.Item>
            </PopupMenu.Menu>
            <PopupMenu.Trigger>
              {({ triggerRef }) => (
                <IconButton
                  ref={triggerRef}
                  Icon={EllipsisHorizontalIcon}
                  text={t('contact_overview.button_more.popup_trigger')}
                />
              )}
            </PopupMenu.Trigger>
          </PopupMenu>
        </div>
      )}
      {!contact.pairedAt ? (
        <div className="flex flex-col grow">
          <hr className="mb-32" />
          <span className="m-auto text-center">{t('contact_overview.text_unpaired_patient')}</span>
        </div>
      ) : (
        <div className="flex flex-col grow">
          {isContactInfoExpanded && (
            <div className="flex flex-col w-full space-y-4">
              <ContactInfoModule
                userId={user.id}
                edit={isEdit}
                handleEdit={handleEdit}
                handleCancelEdit={handleCancelEdit}
                handleAfterSubmit={handleAfterSubmit}
                hideDoB
                hideHeader
              />
            </div>
          )}
          <button
            className="mb-6"
            type="button"
            onClick={() => setIsContactInfoExpanded(!isContactInfoExpanded)}
          >
            <div className="flex flex-row items-center justify-center w-full">
              <div className="border-t border-slate-300 flex-grow mr-4" />
              <span className="text-slate-500">
                {t(
                  `contact_overview.button_expand.${isContactInfoExpanded ? 'expanded' : 'contracted'}`,
                )}
              </span>
              <div className="border-t border-slate-300 flex-grow ml-4" />
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default ContactOverviewModule;
